import * as React from 'react'
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material'
import Index from '../Index'
import {
  VariantDetailGeneInfo,
  VariantDetailGenePLI,
  Variation,
  VariantDetailMatchTranscript
} from '@/@core/services/app.service'
import { OpenInNew, PrinterPosStop } from 'mdi-material-ui'
import { ExternalLinks } from '@/@core/services/api'
import { useTranslation } from 'react-i18next'

const GeneDetail = React.forwardRef(
  (
    props: {
      value: {
        geneInfo: VariantDetailGeneInfo
        genePli: VariantDetailGenePLI
        geneMim: string
        variantInfo: Variation
        transcripts: VariantDetailMatchTranscript[]
        hgncId: number
      }
    },
    ref
  ) => {
    interface Data {
      id: number
      name: string | number
      code: string | number
      size: number | number
      population: number | number
    }
    const column1 = [
      { id: 'name' as const, label: 'Category', minWidth: 170 },
      { id: 'code' as 'name', label: 'Expected SNVs', minWidth: 170 },
      {
        id: 'population' as 'name',
        label: 'Observed SNVs',
        minWidth: 170
      },
      { id: 'size' as 'name', label: 'Constraint metric', minWidth: 170 }
    ]

    const { t, i18n } = useTranslation()

    function createData(
      name: string | number,
      code: string | number,
      population: number | string,
      size: number | string,
      id: number
    ): any {
      return { name, code, population, size, id }
    }

    let rows = [
      createData('Synonymous', 'IE', 4857000, 70273, 7),
      createData('Missense', 'MX', 126577691, 1972550, 8),
      createData('LoF', 'JP', 126317000, 377973, 9)
    ]

    if (props.value.genePli)
      rows = [
        createData(
          'Synonymous',
          props.value.genePli?.syn_exp,
          props.value.genePli?.syn_obs,
          `z=${props.value.genePli?.syn_z}`,
          7
        ),
        createData(
          'Missense',
          `${props.value.genePli?.mis_exp}`,
          props.value.genePli?.mis_obs,
          `z=${props.value.genePli?.mis_z}`,
          8
        ),
        createData(
          'pLoF',
          `${props.value.genePli?.lof_exp}`,
          props.value.genePli?.lof_obs,
          `pLI=${props.value.genePli?.pLI}`,
          9
        )
      ]

    //

    if (!props.value.geneInfo) return <Box></Box>

    const MAX_AF = [
      {
        key: 'Variant & Gene Information:',
        value: `(${props.value?.variantInfo?.gene},${
          props.value?.variantInfo?.c_nomen ?? props.value?.variantInfo?.hgvsc
        },${
          props.value?.variantInfo?.p_nomen ?? props.value?.variantInfo?.hgvsp
        })`
      },
      {
        key: 'Gene name:',
        value: props.value?.variantInfo?.gene
      },
      {
        key: 'Previous symbols:',
        value: props.value.hgncId
      },
      {
        key: 'Gene function:',
        value:
          i18n.language === 'zh'
            ? props.value?.geneInfo.functionZh
            : (props.value?.geneInfo.function as any)
      }
    ]

    return (
      <Box>
        <Stack spacing={2} direction={'row'}>
          <Box width={'100%'}>
            <TableContainer
              sx={{ mb: 6, bgcolor: '#F2F3F5', padding: '10px 5px' }}
            >
              <Table>
                <TableBody>
                  {MAX_AF.map((item) => {
                    if (item.key === 'Gene function:') {
                      return (
                        <React.Fragment>
                          <TableRow key={item?.key}>
                            <TableCell
                              sx={{
                                padding: '0 !important',
                                border: 'none',
                                width: '40%',
                                fontSize: '1rem'
                              }}
                            >
                              <Typography
                                sx={{
                                  '::before': {
                                    content: '""',
                                    display: 'inline-block',
                                    width: 4,
                                    height: '1.25rem',
                                    bgcolor: 'primary.main',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '-10px'
                                  },
                                  lineHeight: '1.25em',
                                  position: 'relative',
                                  ml: '10px',
                                  py: 1
                                }}
                              >
                                {item?.key}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              sx={{
                                padding: '0 !important',
                                border: 'none'
                              }}
                              colSpan={5}
                            >
                              <Typography>{item?.value}</Typography>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      )
                    }

                    return (
                      <React.Fragment>
                        <TableRow key={item?.key}>
                          <TableCell
                            sx={{
                              padding: '0 !important',
                              border: 'none',
                              width: '40%',
                              fontSize: '1rem'
                            }}
                          >
                            <Typography
                              sx={{
                                '::before': {
                                  content: '""',
                                  display: 'inline-block',
                                  width: 4,
                                  height: '1.25rem',
                                  bgcolor: 'primary.main',
                                  position: 'absolute',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  left: '-10px'
                                },
                                lineHeight: '1.25em',
                                position: 'relative',
                                ml: '10px',
                                py: 1
                              }}
                            >
                              {item?.key}
                            </Typography>
                          </TableCell>

                          <TableCell
                            key={item?.key}
                            sx={{
                              padding: '0 !important',
                              border: 'none'
                            }}
                          >
                            <Stack
                              direction={'row'}
                              alignItems="center"
                              justifyContent={'space-between'}
                            >
                              <Typography>{item?.value}</Typography>

                              {item.key === 'Gene name:' ? (
                                <Button
                                  endIcon={<OpenInNew />}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    if (
                                      item.key === 'Gene name:' &&
                                      item.value
                                    ) {
                                      window.open(
                                        `${ExternalLinks.decipher}/${item.value}/overview/protein-genomic-info`
                                      )
                                    }
                                  }}
                                  sx={{
                                    textTransform: 'capitalize',
                                    width: '140px',
                                    fontSize: 18,
                                    fontWeight: 600,
                                    borderWidth: '2px',
                                    ':hover': {
                                      borderWidth: '2px'
                                    }
                                  }}
                                >
                                  decipher
                                </Button>
                              ) : (
                                ''
                              )}

                              {item.key === 'Variant & Gene Information:' ? (
                                <Button
                                  endIcon={<OpenInNew />}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    if (
                                      item.key ===
                                        'Variant & Gene Information:' &&
                                      item.value
                                    ) {
                                      window.open(
                                        `${ExternalLinks.marrvel}/${props.value.variantInfo.chrom}:${props.value.variantInfo.pos}${props.value.variantInfo.nuc_change}`
                                      )
                                    }
                                  }}
                                  sx={{
                                    textTransform: 'capitalize',
                                    width: '140px',
                                    fontSize: 18,
                                    fontWeight: 600,
                                    borderWidth: '2px',
                                    ':hover': {
                                      borderWidth: '2px'
                                    }
                                  }}
                                >
                                  marrvel
                                </Button>
                              ) : (
                                ''
                              )}

                              {item.key === 'Previous symbols:' ? (
                                <Button
                                  endIcon={<OpenInNew />}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    if (
                                      item.key === 'Previous symbols:' &&
                                      item.value
                                    ) {
                                      window.open(
                                        `${ExternalLinks.Clingen}${item.value}`
                                      )
                                    }
                                  }}
                                  sx={{
                                    textTransform: 'capitalize',
                                    width: '140px',
                                    fontSize: 18,
                                    fontWeight: 600,
                                    borderWidth: '2px',
                                    ':hover': {
                                      borderWidth: '2px'
                                    }
                                  }}
                                >
                                  Clingen
                                </Button>
                              ) : (
                                ''
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* {props.value.genePli ? <Index columns={column1} rows={rows} /> : ''} */}

            {/* <Index columns={column2} rows={props.value.transcripts} /> */}
          </Box>
        </Stack>
      </Box>
    )
  }
)

export default GeneDetail
